/**
 * DatabasePerformance - Painel de Monitoramento de Performance do Banco
 * 
 * Dashboard com 4 abas para monitorar:
 * - Performance: métricas de seq_scans vs idx_scans por tabela
 * - Índices: estatísticas de uso de índices
 * - Bloat: tabelas com tuplas mortas que precisam de VACUUM
 * - Manutenção: snapshots e histórico
 */

import { useState } from 'react';
import { 
  Database, 
  RefreshCw, 
  Activity, 
  HardDrive, 
  Trash2, 
  Camera,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  HelpCircle,
  Zap,
  MemoryStick,
  BarChart3
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useDbPerformance } from '@/hooks/useDbPerformance';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { showToast } from '@/lib/toast';

// =====================================================
// COMPONENTES AUXILIARES
// =====================================================

/**
 * Card de KPI com indicador de status
 */
function KpiCard({ 
  title, 
  value, 
  description, 
  status,
  icon: Icon 
}: { 
  title: string;
  value: string | number;
  description?: string;
  status: 'success' | 'warning' | 'error' | 'neutral';
  icon: React.ElementType;
}) {
  const statusColors = {
    success: 'text-green-500',
    warning: 'text-yellow-500',
    error: 'text-red-500',
    neutral: 'text-muted-foreground',
  };

  const statusBg = {
    success: 'bg-green-500/10',
    warning: 'bg-yellow-500/10',
    error: 'bg-red-500/10',
    neutral: 'bg-muted/50',
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            {description && (
              <p className="text-xs text-muted-foreground">{description}</p>
            )}
          </div>
          <div className={`p-3 rounded-full ${statusBg[status]}`}>
            <Icon className={`h-5 w-5 ${statusColors[status]}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Badge de status com cor baseada no valor
 */
function StatusBadge({ value, thresholds }: { 
  value: number; 
  thresholds: { success: number; warning: number };
}) {
  let variant: 'default' | 'secondary' | 'destructive' = 'default';
  let label = 'Saudável';

  if (value < thresholds.warning) {
    variant = 'destructive';
    label = 'Crítico';
  } else if (value < thresholds.success) {
    variant = 'secondary';
    label = 'Atenção';
  }

  return (
    <Badge variant={variant} className="ml-2">
      {value.toFixed(2)}% - {label}
    </Badge>
  );
}

/**
 * Converte valor string | number para número
 */
function toNumber(val: string | number | undefined): number {
  if (typeof val === 'number') return val;
  if (typeof val === 'string') return parseFloat(val) || 0;
  return 0;
}

/**
 * Formata números grandes (ex: 108746135 -> 108.7M)
 */
function formatLargeNumber(num: number): string {
  if (num >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(1)}B`;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toString();
}

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

export default function DatabasePerformance() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('performance');
  const [showHelpModal, setShowHelpModal] = useState(false);
  
  const {
    performance,
    indexes,
    bloat,
    connections,
    snapshots,
    kpis,
    isLoading,
    isLoadingSnapshots,
    isSavingSnapshot,
    refetchAll,
    saveSnapshot,
  } = useDbPerformance();

  // Filtra tabelas pelo termo de busca
  const filteredPerformance = performance.filter(t => 
    t.table_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredIndexes = indexes.filter(i => 
    i.table_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.index_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredBloat = bloat.filter(t => 
    t.table_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handler para salvar snapshot
  const handleSaveSnapshot = () => {
    saveSnapshot(undefined, {
      onSuccess: () => {
        showToast.success('Snapshot salvo com sucesso');
      },
      onError: (error) => {
        showToast.error('Erro ao salvar snapshot', error.message);
      }
    });
  };

  // Determina status dos KPIs
  const getCacheStatus = () => {
    const val = parseFloat(kpis.avgCacheHitRatio);
    if (val >= 99) return 'success';
    if (val >= 95) return 'warning';
    return 'error';
  };

  const getIndexStatus = () => {
    const val = parseFloat(kpis.avgIndexUsageRatio);
    if (val >= 80) return 'success';
    if (val >= 50) return 'warning';
    return 'error';
  };

  const getConnectionStatus = () => {
    if (kpis.connectionUsage < 70) return 'success';
    if (kpis.connectionUsage < 90) return 'warning';
    return 'error';
  };

  const getVacuumStatus = () => {
    if (kpis.tablesNeedingVacuum === 0) return 'success';
    if (kpis.tablesNeedingVacuum <= 3) return 'warning';
    return 'error';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="h-6 w-6" />
            Performance do Banco de Dados
          </h1>
          <p className="text-muted-foreground">
            Monitoramento de índices, cache e estatísticas
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowHelpModal(true)}
          >
            <HelpCircle className="h-4 w-4 mr-2" />
            Ajuda
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetchAll()}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleSaveSnapshot}
            disabled={isSavingSnapshot}
          >
            <Camera className="h-4 w-4 mr-2" />
            Salvar Snapshot
          </Button>
        </div>
      </div>

      {/* Modal de Ajuda - Guia Didático das Métricas */}
      <Dialog open={showHelpModal} onOpenChange={setShowHelpModal}>
        <DialogContent className="max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-primary" />
              Guia de Métricas de Performance
            </DialogTitle>
            <DialogDescription>
              Entenda o significado de cada métrica e como melhorar a performance do banco
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="h-[60vh] pr-4">
            <div className="space-y-6">
              {/* Seq Scans */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-full bg-destructive/10">
                    <AlertTriangle className="h-4 w-4 text-destructive" />
                  </div>
                  <h3 className="font-semibold text-lg">Seq Scans (Sequential Scans)</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Quantidade de vezes que o PostgreSQL percorreu a <strong>tabela inteira</strong> para responder uma query.
                </p>
                <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                  <p className="text-sm font-medium">Quando acontece:</p>
                  <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                    <li>Não existe índice para a coluna consultada</li>
                    <li>Existe índice mas não está sendo usado</li>
                    <li>O planner do PostgreSQL decidiu que o índice não compensa</li>
                    <li>A tabela é muito pequena (às vezes é normal)</li>
                  </ul>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Badge variant="destructive">⚠️ Regra</Badge>
                  <span>Quanto maior o número de seq scans, maior o risco de lentidão.</span>
                </div>
              </div>

              <Separator />

              {/* Idx Scans */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-full bg-green-500/10">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  </div>
                  <h3 className="font-semibold text-lg">Idx Scans (Index Scans)</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Número de vezes que o Postgres <strong>usou um índice</strong> para encontrar os dados de forma eficiente.
                </p>
                <div className="flex items-center gap-2 text-sm">
                  <Badge className="bg-green-500/10 text-green-600 hover:bg-green-500/20">✅ Regra</Badge>
                  <span>Quanto maior esse número, melhor a performance.</span>
                </div>
              </div>

              <Separator />

              {/* Linhas Est. */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-full bg-muted">
                    <BarChart3 className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <h3 className="font-semibold text-lg">Linhas Est. (Estimated Rows)</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Estimativa de linhas retornadas por scan — usado para verificar se as estatísticas estão atualizadas.
                </p>
                <div className="bg-yellow-500/10 rounded-lg p-3">
                  <p className="text-sm">
                    <strong>⚠️ Alerta:</strong> Se for 0 ou muito baixa, pode indicar falta de <code className="bg-muted px-1 rounded">ANALYZE</code>.
                  </p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-sm font-medium mb-1">Solução:</p>
                  <code className="text-xs bg-background p-2 rounded block">
                    VACUUM ANALYZE nome_da_tabela;
                  </code>
                </div>
              </div>

              <Separator />

              {/* Cache Hit */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-full bg-blue-500/10">
                    <MemoryStick className="h-4 w-4 text-blue-500" />
                  </div>
                  <h3 className="font-semibold text-lg">Cache Hit (%)</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Percentual de vezes que o Postgres encontrou os dados na <strong>memória</strong>, sem precisar ir ao disco.
                </p>
                <div className="bg-muted/50 rounded-lg p-3">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-24">Valor</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell><Badge className="bg-green-500/10 text-green-600">≥ 99%</Badge></TableCell>
                        <TableCell className="text-sm">🟢 Excelente</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><Badge variant="secondary">80-99%</Badge></TableCell>
                        <TableCell className="text-sm">🟡 Aceitável</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><Badge variant="destructive">&lt; 80%</Badge></TableCell>
                        <TableCell className="text-sm">🔴 Risco de lentidão</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><Badge variant="outline">0%</Badge></TableCell>
                        <TableCell className="text-sm">Tabela pequena ou sem cache</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>

              <Separator />

              {/* Idx Usage - A MAIS IMPORTANTE */}
              <div className="space-y-2 bg-primary/5 -mx-4 px-4 py-4 rounded-lg border border-primary/20">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-full bg-primary/20">
                    <Zap className="h-4 w-4 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg">Idx Usage (%) — ⚡ A MAIS IMPORTANTE</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Percentual de buscas que usaram índice ao invés de percorrer a tabela inteira.
                </p>
                <div className="bg-background rounded-lg p-3 border">
                  <p className="text-sm font-medium mb-1">Fórmula:</p>
                  <code className="text-sm bg-muted px-2 py-1 rounded block text-center font-mono">
                    idx_usage = index_scans / (index_scans + seq_scans)
                  </code>
                </div>
                <div className="bg-background rounded-lg p-3 border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-24">Valor</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell><Badge className="bg-green-500/10 text-green-600">&gt; 90%</Badge></TableCell>
                        <TableCell className="text-sm">🟢 Excelente</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><Badge variant="secondary">50-90%</Badge></TableCell>
                        <TableCell className="text-sm">🟡 Aceitável</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><Badge variant="destructive">&lt; 50%</Badge></TableCell>
                        <TableCell className="text-sm">🔴 Problema</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><Badge variant="destructive">0-10%</Badge></TableCell>
                        <TableCell className="text-sm">🔴🔴 CRÍTICO - tabela não usa índice</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
                <div className="bg-background rounded-lg p-3 border space-y-2">
                  <p className="text-sm font-medium">Como resolver baixo Idx Usage:</p>
                  <ol className="text-sm text-muted-foreground list-decimal list-inside space-y-1">
                    <li>Identificar as queries que mais acessam a tabela</li>
                    <li>Verificar se há índices nas colunas do WHERE</li>
                    <li>Criar índices compostos se necessário</li>
                    <li>Executar ANALYZE para atualizar estatísticas</li>
                  </ol>
                  <div className="mt-2 space-y-1">
                    <code className="text-xs bg-muted p-2 rounded block">
                      -- Criar índice simples<br/>
                      CREATE INDEX idx_tabela_coluna ON tabela(coluna);
                    </code>
                    <code className="text-xs bg-muted p-2 rounded block">
                      -- Criar índice composto<br/>
                      CREATE INDEX idx_tabela_col1_col2 ON tabela(col1, col2);
                    </code>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Ações Recomendadas */}
              <div className="space-y-2">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Ações Recomendadas
                </h3>
                <div className="bg-muted/50 rounded-lg p-3">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Problema</TableHead>
                        <TableHead>Ação</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="text-sm">Idx Usage baixo</TableCell>
                        <TableCell className="text-sm">Criar índices nas colunas mais consultadas</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-sm">Cache Hit baixo</TableCell>
                        <TableCell className="text-sm">Aumentar <code className="bg-muted px-1 rounded">shared_buffers</code> ou otimizar queries</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-sm">Muitos Seq Scans</TableCell>
                        <TableCell className="text-sm">Verificar queries sem cláusula WHERE indexada</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-sm">Linhas Est. = 0</TableCell>
                        <TableCell className="text-sm">Executar <code className="bg-muted px-1 rounded">VACUUM ANALYZE</code> na tabela</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="text-sm">Muitas tuplas mortas</TableCell>
                        <TableCell className="text-sm">Executar <code className="bg-muted px-1 rounded">VACUUM</code> na tabela</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {isLoading ? (
          <>
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
          </>
        ) : (
          <>
            <KpiCard
              title="Cache Hit Rate"
              value={`${kpis.avgCacheHitRatio}%`}
              description="Dados servidos da memória"
              status={getCacheStatus()}
              icon={Activity}
            />
            <KpiCard
              title="Uso de Índices"
              value={`${kpis.avgIndexUsageRatio}%`}
              description={`${kpis.criticalTables} tabela(s) crítica(s)`}
              status={getIndexStatus()}
              icon={HardDrive}
            />
            <KpiCard
              title="Conexões"
              value={connections ? `${connections.active_connections}/${connections.max_connections}` : '-'}
              description={`${kpis.connectionUsage.toFixed(1)}% em uso`}
              status={getConnectionStatus()}
              icon={Database}
            />
            <KpiCard
              title="Tabelas p/ VACUUM"
              value={kpis.tablesNeedingVacuum}
              description=">10% tuplas mortas"
              status={getVacuumStatus()}
              icon={Trash2}
            />
          </>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="performance">Performance</TabsTrigger>
            <TabsTrigger value="indexes">Índices</TabsTrigger>
            <TabsTrigger value="bloat">Bloat</TabsTrigger>
            <TabsTrigger value="maintenance">Manutenção</TabsTrigger>
          </TabsList>
          
          {activeTab !== 'maintenance' && (
            <Input
              placeholder="Filtrar tabelas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64"
            />
          )}
        </div>

        {/* Tab: Performance */}
        <TabsContent value="performance" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Performance por Tabela</CardTitle>
              <CardDescription>
                Comparativo de Sequential Scans vs Index Scans. 
                Índice baixo (&lt;50%) indica que a tabela não está usando índices corretamente.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12" />
                  ))}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tabela</TableHead>
                      <TableHead className="text-right">Seq Scans</TableHead>
                      <TableHead className="text-right">Idx Scans</TableHead>
                      <TableHead className="text-right">Linhas Est.</TableHead>
                      <TableHead className="text-right">Cache Hit</TableHead>
                      <TableHead className="text-right">Idx Usage</TableHead>
                      <TableHead>Último Analyze</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPerformance.map((table) => {
                      const idxUsage = toNumber(table.index_usage_ratio);
                      const cacheHit = toNumber(table.cache_hit_ratio);
                      return (
                        <TableRow key={table.table_name}>
                          <TableCell className="font-medium">{table.table_name}</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatLargeNumber(table.seq_scans)}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatLargeNumber(table.idx_scans)}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatLargeNumber(table.rows_estimate)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant={cacheHit >= 99 ? 'default' : 'secondary'}>
                              {cacheHit.toFixed(2)}%
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              {idxUsage < 50 ? (
                                <AlertTriangle className="h-4 w-4 text-destructive" />
                              ) : idxUsage < 80 ? (
                                <AlertTriangle className="h-4 w-4 text-yellow-500" />
                              ) : (
                                <CheckCircle className="h-4 w-4 text-green-500" />
                              )}
                              <span className={
                                idxUsage < 50 ? 'text-destructive font-medium' :
                                idxUsage < 80 ? 'text-yellow-600' : 'text-green-600'
                              }>
                                {idxUsage.toFixed(2)}%
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {table.last_analyze 
                              ? format(new Date(table.last_analyze), "dd/MM HH:mm", { locale: ptBR })
                              : '-'
                            }
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Índices */}
        <TabsContent value="indexes" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Estatísticas de Índices</CardTitle>
              <CardDescription>
                Uso de cada índice. Índices com idx_scan = 0 podem ser candidatos à remoção.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12" />
                  ))}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tabela</TableHead>
                      <TableHead>Índice</TableHead>
                      <TableHead className="text-right">Tamanho</TableHead>
                      <TableHead className="text-right">Scans</TableHead>
                      <TableHead className="text-right">Tuplas Lidas</TableHead>
                      <TableHead className="text-right">Tuplas Fetch</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredIndexes.map((idx, i) => (
                      <TableRow key={`${idx.index_name}-${i}`}>
                        <TableCell className="font-medium">{idx.table_name}</TableCell>
                        <TableCell className="font-mono text-sm">
                          {idx.index_name}
                          {idx.idx_scan === 0 && (
                            <Badge variant="destructive" className="ml-2">Não usado</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{idx.index_size}</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatLargeNumber(idx.idx_scan)}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatLargeNumber(idx.idx_tup_read)}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatLargeNumber(idx.idx_tup_fetch)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Bloat */}
        <TabsContent value="bloat" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Tabelas com Tuplas Mortas (Bloat)</CardTitle>
              <CardDescription>
                Tabelas com mais de 10% de tuplas mortas precisam de VACUUM ANALYZE.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12" />
                  ))}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tabela</TableHead>
                      <TableHead className="text-right">Tuplas Vivas</TableHead>
                      <TableHead className="text-right">Tuplas Mortas</TableHead>
                      <TableHead className="text-right">% Mortas</TableHead>
                      <TableHead>Último Vacuum</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBloat.map((table) => (
                      <TableRow key={table.table_name}>
                        <TableCell className="font-medium">{table.table_name}</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatLargeNumber(table.live_tuples)}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatLargeNumber(table.dead_tuples)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Progress 
                              value={Math.min(table.dead_ratio, 100)} 
                              className="w-16 h-2"
                            />
                            <span className={
                              table.dead_ratio > 10 ? 'text-destructive font-medium' :
                              table.dead_ratio > 5 ? 'text-yellow-600' : ''
                            }>
                              {table.dead_ratio.toFixed(2)}%
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {table.last_vacuum 
                            ? format(new Date(table.last_vacuum), "dd/MM HH:mm", { locale: ptBR })
                            : table.last_autovacuum
                              ? format(new Date(table.last_autovacuum), "dd/MM HH:mm", { locale: ptBR }) + ' (auto)'
                              : 'Nunca'
                          }
                        </TableCell>
                        <TableCell>
                          {table.needs_vacuum ? (
                            <Badge variant="destructive" className="flex items-center gap-1 w-fit">
                              <XCircle className="h-3 w-3" />
                              Precisa VACUUM
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="flex items-center gap-1 w-fit">
                              <CheckCircle className="h-3 w-3" />
                              OK
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Manutenção */}
        <TabsContent value="maintenance" className="mt-4">
          <div className="grid gap-6">
            {/* Info sobre pg_cron */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  VACUUM ANALYZE Semanal
                </CardTitle>
                <CardDescription>
                  O pg_cron job executa VACUUM ANALYZE nas tabelas críticas toda segunda-feira às 02:00.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-muted/50 p-4 rounded-lg">
                  <p className="text-sm font-mono mb-2">Tabelas incluídas:</p>
                  <div className="flex flex-wrap gap-2">
                    {['tenant_members', 'user_roles', 'profiles', 'tenants', 
                      'inboxes', 'conversations', 'messages', 'contacts'].map(t => (
                      <Badge key={t} variant="outline">{t}</Badge>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-4">
                    Para verificar o status do job, acesse o SQL Editor do Supabase e execute:
                    <code className="block bg-background p-2 rounded mt-1">
                      SELECT * FROM cron.job WHERE jobname = 'weekly-vacuum-analyze-critical-tables';
                    </code>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Snapshots */}
            <Card>
              <CardHeader>
                <CardTitle>Histórico de Snapshots</CardTitle>
                <CardDescription>
                  Snapshots manuais para comparação de métricas ao longo do tempo.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingSnapshots ? (
                  <div className="space-y-2">
                    {[...Array(3)].map((_, i) => (
                      <Skeleton key={i} className="h-12" />
                    ))}
                  </div>
                ) : snapshots.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Camera className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Nenhum snapshot salvo ainda.</p>
                    <p className="text-sm">Clique em "Salvar Snapshot" para criar o primeiro.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Cache Hit Médio</TableHead>
                        <TableHead>Idx Usage Médio</TableHead>
                        <TableHead>Conexões</TableHead>
                        <TableHead>Notas</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {snapshots.map((snapshot) => {
                        // Cast do snapshot_data para o tipo esperado
                        const snapshotData = snapshot.snapshot_data as {
                          performance?: Array<{ cache_hit_ratio?: string | number; index_usage_ratio?: string | number }>;
                          connections?: { active_connections?: number; max_connections?: number };
                        } | null;
                        
                        const perfData = snapshotData?.performance || [];
                        const avgCache = perfData.length 
                          ? (perfData.reduce((s, t) => s + toNumber(t.cache_hit_ratio), 0) / perfData.length).toFixed(2)
                          : '-';
                        const avgIdx = perfData.length
                          ? (perfData.reduce((s, t) => s + toNumber(t.index_usage_ratio), 0) / perfData.length).toFixed(2)
                          : '-';
                        const conn = snapshotData?.connections;
                        
                        return (
                          <TableRow key={snapshot.id}>
                            <TableCell className="font-medium">
                              {format(new Date(snapshot.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{snapshot.snapshot_type}</Badge>
                            </TableCell>
                            <TableCell>{avgCache}%</TableCell>
                            <TableCell>{avgIdx}%</TableCell>
                            <TableCell>
                              {conn ? `${conn.active_connections}/${conn.max_connections}` : '-'}
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm">
                              {snapshot.notes || '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
